/**
 * DisperseEngine — the core offset calculator.
 *
 * Given an array of marker descriptors (pixel positions, sizes, anchors),
 * computes pixel offsets so that no markers' bounding boxes overlap.
 *
 * The engine is library-agnostic: it knows nothing about Leaflet, the DOM,
 * or geographic coordinates. It operates entirely in pixel space.
 *
 * Dispersal patterns are pluggable via the `pattern` option, which
 * accepts a string name (looked up in the built-in registry) or a custom
 * function conforming to the DispersePatternFn signature.
 */

import { findOverlapGroups } from './spatialIndex.js';
import { circlePattern } from './patterns/circlePattern.js';
import { gridPattern } from './patterns/gridPattern.js';
import { hexgridPattern } from './patterns/hexgridPattern.js';

/**
 * @typedef {Object} MarkerDescriptor
 * @property {string|number} id - Unique identifier for the marker
 * @property {number} x - Pixel x of anchor point
 * @property {number} y - Pixel y of anchor point
 * @property {number} width - Bounding box width in px
 * @property {number} height - Bounding box height in px
 * @property {number} anchorX - Anchor offset from top-left of bounding box
 * @property {number} anchorY - Anchor offset from top-left of bounding box
 */

/**
 * @typedef {Object} MarkerOffset
 * @property {number} dx - Pixel offset in x
 * @property {number} dy - Pixel offset in y
 */

/**
 * A dispersal pattern function. Given a group of overlapping markers
 * and the minimum spacing, returns a map of marker id → offset.
 *
 * @callback DispersePatternFn
 * @param {MarkerDescriptor[]} group - Markers that overlap each other
 * @param {number} spacing - Minimum gap in px between bounding boxes
 * @returns {Map<string|number, MarkerOffset>}
 */

// Built-in pattern registry
const patternRegistry = new Map();
patternRegistry.set('circle', circlePattern);
patternRegistry.set('grid', gridPattern);
patternRegistry.set('hexgrid', hexgridPattern);

/**
 * Register a custom dispersal pattern for use by name.
 * @param {string} name
 * @param {DispersePatternFn} fn
 */
export function registerPattern(name, fn) {
  patternRegistry.set(name, fn);
}

/**
 * @typedef {Object} DisperseOptions
 * @property {number} [minimumSpacing=0] - Pixel gap required between marker bounding boxes
 * @property {string|DispersePatternFn} [pattern='circle'] - Dispersal pattern name or function
 */

/**
 * Calculate pixel offsets for all markers so that overlapping groups are dispersed.
 *
 * Dispersing one group may push markers into overlap with markers from
 * other groups (or previously non-overlapping markers). To handle this,
 * the engine iterates: after each pass it re-checks all markers at their
 * dispersed positions and re-disperses any new overlap groups, until no
 * overlaps remain (or a maximum iteration count is reached).
 *
 * @param {MarkerDescriptor[]} markers
 * @param {DisperseOptions} [options={}]
 * @returns {Object.<string|number, MarkerOffset>} Map of marker id → { dx, dy }
 */
export function disperse(markers, options = {}) {
  const { minimumSpacing = 0, pattern = 'circle', maxIterations = 10 } = options;

  // Resolve the pattern function
  let patternFn;
  if (typeof pattern === 'function') {
    patternFn = pattern;
  } else {
    patternFn = patternRegistry.get(pattern);
    if (!patternFn) {
      throw new Error(
        `Unknown dispersal pattern: "${pattern}". ` +
          `Available patterns: ${Array.from(patternRegistry.keys()).join(', ')}. ` +
          `You can also pass a custom function.`
      );
    }
  }

  // Build result: default all markers to zero offset
  const offsets = {};
  for (const m of markers) {
    offsets[m.id] = { dx: 0, dy: 0 };
  }

  // Build a lookup for original markers by id
  const markerById = new Map();
  for (const m of markers) {
    markerById.set(m.id, m);
  }

  // Iteratively disperse until no overlaps remain
  for (let iteration = 0; iteration < maxIterations; iteration++) {
    // Create marker descriptors at their current dispersed positions
    const currentMarkers = markers.map((m) => ({
      ...m,
      x: m.x + offsets[m.id].dx,
      y: m.y + offsets[m.id].dy,
    }));

    // Find groups of overlapping markers at current positions
    const groups = findOverlapGroups(currentMarkers, minimumSpacing);

    // If no overlaps, we're done
    if (groups.length === 0) {
      break;
    }

    // For each overlap group, compute dispersal offsets from their
    // current (already-offset) positions
    for (const group of groups) {
      const groupOffsets = patternFn(group, minimumSpacing);
      for (const [id, offset] of groupOffsets) {
        // Accumulate: the pattern returns offsets relative to the
        // current (already-offset) positions, so we add them to the
        // existing offsets from the original positions.
        const original = markerById.get(id);
        offsets[id] = {
          dx: original.x + offsets[id].dx + offset.dx - original.x,
          dy: original.y + offsets[id].dy + offset.dy - original.y,
        };
      }
    }
  }

  return offsets;
}
